import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TachographCompanyCardGrid } from "./components/TachographCompanyCardGrid";
import { TachographDataStatus } from "./components/TachographDataStatus";
import { TachographDownloadsPanel } from "./components/TachographDownloadsPanel";
import { TachographDriverGrid } from "./components/TachographDriverGrid";
import { TachographDueSettingsButton } from "./components/TachographDueSettingsButton";
import { TachographSummaryCards } from "./components/TachographSummaryCards";
import { TachographVehicleGrid } from "./components/TachographVehicleGrid";
import { useTachographFileDownload } from "./hooks/useTachographFileDownload";
import { useTachographPageData } from "./hooks/useTachographPageData";
import { TACHOGRAPH_TABS, TachographSummaryFilter, TachographTab } from "./models/Tachograph";
import { selectVehicleRows } from "./utilities/tachographRows";
import { MAX_USERS_LOADED, RESULTS_LIMIT_DEVICE } from "@/constants/resultsLimits";
import { useDebounced } from "@/hooks/useDebounced";

const SEARCH_DEBOUNCE_MS = 400;

export function TachographPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TachographTab>("status");
  const [summaryFilter, setSummaryFilter] = useState<TachographSummaryFilter>("all");
  const [search, setSearch] = useState("");

  /** A counter narrows down the vehicle list, which only the status tab shows in full. */
  const selectSummaryFilter = (filter: TachographSummaryFilter) => {
    setSummaryFilter(current => (current === filter ? "all" : filter));
    setActiveTab("status");
  };

  /** Switching tabs drops the counter filter so two filters never stack unseen. */
  const selectTab = (tab: TachographTab) => {
    setActiveTab(tab);
    setSummaryFilter("all");
  };

  /** Every keystroke would otherwise re-fire the file, status and trip queries. */
  const debouncedSearch = useDebounced(search, SEARCH_DEBOUNCE_MS);

  const {
    vehicleRows,
    driverRows,
    downloadRows,
    companyCardRows,
    summary,
    dueSettings,
    updateDueSettings,
    resetDueSettings,
    geotabPolicy,
    filesPartial,
    devicesTruncated,
    driversTruncated,
    canShowMissingDeviceInfo,
    diagnosticsMissing,
    exportDisabled,
    loadFailed,
    isLoadingDueSettings,
    isLoadingDeviceInfo,
    isLoadingDevices,
    isLoadingFiles,
    isLoadingVehicles,
    isLoadingDrivers,
    isLoadingAssignments,
    isLoadingCompanyCards,
    companyCardsTruncated,
    loadingParts,
    failedParts,
  } = useTachographPageData(debouncedSearch);
  const { downloadFiles, isDownloading } = useTachographFileDownload();

  const visibleVehicleRows = useMemo(
    () => selectVehicleRows(vehicleRows, activeTab, summaryFilter),
    [activeTab, summaryFilter, vehicleRows],
  );


  return (
    <Stack spacing={2} sx={{ height: "100%", minHeight: 0, boxSizing: "border-box" }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t("tachograph.nativeTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("tachograph.nativeDescription")}
        </Typography>
      </Box>

      <TachographDataStatus
        loadingParts={loadingParts}
        failedParts={failedParts}
        companyCardsTruncated={companyCardsTruncated}
      />
      {devicesTruncated ? (
        <Alert severity="warning">
          {t("tachograph.devicesTruncated", { count: RESULTS_LIMIT_DEVICE })}
        </Alert>
      ) : null}
      {driversTruncated ? (
        <Alert severity="warning">
          {t("tachograph.driversTruncated", { count: MAX_USERS_LOADED })}
        </Alert>
      ) : null}
      {filesPartial ? (
        <Alert severity="warning">{t("tachograph.dataPartial")}</Alert>
      ) : null}

      <TachographSummaryCards
        summary={summary}
        activeFilter={summaryFilter}
        onSelect={selectSummaryFilter}
        warningDays={dueSettings.warningDays}
      />

      <Paper
        variant="outlined"
        sx={{ p: 1.5, borderRadius: 2, flex: 1, minHeight: 0, display: "flex" }}
      >
        <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0, width: "100%" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", md: "center" }}
            justifyContent="space-between"
          >
            <Tabs
              value={activeTab}
              onChange={(_event, value: TachographTab) => selectTab(value)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {TACHOGRAPH_TABS.map(tab => (
                <Tab key={tab} value={tab} label={t(`tachograph.tabs.${tab}`)} />
              ))}
            </Tabs>

            <TextField
              size="small"
              label={t("tachograph.search")}
              value={search}
              onChange={event => setSearch(event.target.value)}
              sx={{ minWidth: { xs: "100%", md: 280 } }}
            />
            <TachographDueSettingsButton
              settings={dueSettings}
              onChange={updateDueSettings}
              onReset={resetDueSettings}
              geotabPolicy={geotabPolicy}
            />
          </Stack>

          <Box sx={{ flex: 1, minHeight: 520 }}>
            {activeTab === "driver" ? (
              <TachographDriverGrid
                rows={driverRows}
                loading={
                  isLoadingDevices ||
                  isLoadingDrivers ||
                  isLoadingAssignments ||
                  isLoadingFiles ||
                  isLoadingDueSettings ||
                  isLoadingDeviceInfo
                }
                exportDisabled={exportDisabled.drivers}
                loadFailed={loadFailed.drivers}
                highlightRows={dueSettings.highlightRows}
              />
            ) : activeTab === "companyCards" ? (
              <TachographCompanyCardGrid
                rows={companyCardRows}
                loading={isLoadingCompanyCards}
                exportDisabled={exportDisabled.companyCards}
                loadFailed={loadFailed.companyCards}
              />
            ) : activeTab === "downloads" ? (
              <TachographDownloadsPanel
                rows={downloadRows}
                loading={isLoadingDevices || isLoadingFiles}
                exportDisabled={exportDisabled.downloads}
                loadFailed={loadFailed.downloads}
                isDownloading={isDownloading}
                onDownload={downloadFiles}
              />
            ) : (
              <TachographVehicleGrid
                tab={activeTab}
                rows={visibleVehicleRows}
                loading={isLoadingVehicles}
                exportDisabled={exportDisabled.vehicles}
                canShowMissing={canShowMissingDeviceInfo}
                loadFailed={loadFailed.vehicles}
                diagnosticsMissing={diagnosticsMissing}
                highlightRows={dueSettings.highlightRows}
              />
            )}
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
}
